// Boot: decide, before any app code downloads, whether this visitor gets the
// marketing page or the app (mooshroom decisions D25; Taiga
// docs/mooshroom-handoff/r7-r8.md T5).
//
// A signed-out visitor at `/` or `/download` on the apex sees only the marketing
// page, so they should download only the marketing page — not the ~730 kB app
// bundle. The app (`./App`) is a lazy chunk; this file and `PublicRoot` are all
// that is in the entry bundle besides React and the router.
//
// Anything we are unsure about goes to the app, which handles every case itself:
// the `my.` and `admin.` hosts, an impersonation hand-off (`?_imp=`), a network
// failure, or any answer from /api/me/ other than 200 or 401.
//
// The apex also serves sign-in and the legal pages. Any other path on it is an
// app or console path from before the host split, so it moves to `my.` or
// `admin.` here, before the app downloads (services/hosts.js).

import { hostForPath } from './services/hosts';

// The public site's pages (components/pages/marketing/Site.js). A signed-in
// user at `/` or `/download` gets the app's page there; at any other site path
// they get the site with a Dashboard button (App.js).
export const MARKETING_PATHS = new Set(['/', '/download', '/features', '/agents', '/morel', '/access', '/help']);

/** A page of the public site. */
export const isPublicPath = (pathname) => MARKETING_PATHS.has(pathname)
  || pathname.startsWith('/help/');

const API = process.env.REACT_APP_API_URL || '';

// Pages of the apex that are not the public site. Kept in step with
// ApexRoutes in App.js.
const APEX_APP_PATHS = new Set(['/login', '/forgot-password', '/terms', '/privacy']);

/** On the apex, the [host kind, path] a path moves to, or null if it stays. */
export function apexMove(loc, kind) {
  if (kind !== 'apex') return null;
  const { pathname } = loc;
  if (isPublicPath(pathname) || APEX_APP_PATHS.has(pathname) || pathname.startsWith('/register')) {
    return null;
  }
  return hostForPath(pathname + (loc.search || '') + (loc.hash || ''));
}

/** Is this a page a signed-out visitor might get the marketing page on? */
export function marketingCandidate(loc, kind) {
  if (kind !== 'apex') return false;
  if (!isPublicPath(loc.pathname)) return false;
  return !new URLSearchParams(loc.search).has('_imp');
}

/** Resolves true when the visitor is signed out (401 from /api/me/), false when
 *  signed in or when the answer is anything else. Also primes the CSRF cookie,
 *  which the request-access form needs and the app's AuthProvider would
 *  otherwise have set. */
export async function signedOut(fetchImpl = window.fetch.bind(window)) {
  const opts = { credentials: 'include', headers: { Accept: 'application/json' } };
  try {
    const [, me] = await Promise.all([
      fetchImpl(`${API}/api/csrf/`, opts).catch(() => null),
      fetchImpl(`${API}/api/me/`, opts),
    ]);
    return me.status === 401;
  } catch (_) {
    return false;
  }
}
